/**
 * THE ATTACHMENT PICKER'S SHARED VOCABULARY — `tasks/practice-documents.md` §14.3, §14.4, §24.2.
 *
 * Three things live here and nowhere else: the CONTEXT enum, the DIALOG CONTRACT, and the two
 * pure rules — what a context may offer, and what a context pre-ticks.
 *
 * ── WHY A MODULE OF ITS OWN RATHER THAN THE COMPONENT FILE ───────────────────────────────────
 *
 * Three files need this vocabulary and they form a chain: the picker opens the dialog, and the
 * service opens the dialog on behalf of a host that has no picker (a menu item — §14.3's WhatsApp
 * row). If the contract lived in the dialog, the service would import the dialog for the contract
 * AND for the class; if it lived in the service, the dialog would import the service and the
 * service the dialog. One leaf module with no Angular in it makes both cycles impossible.
 *
 * ── THE TWO RULES ARE FUNCTIONS, NOT METHODS, DELIBERATELY ───────────────────────────────────
 *
 * `isOfferable` and `preTickedIds` decide what a user is shown and what is ticked before they
 * touch anything. Both the chip row and the dialog have to agree about them exactly — a dialog
 * that offered a document the chip row's pre-tick had already excluded would be two answers to one
 * question. As free functions there is one implementation and no instance to be out of date.
 */

import { PracticeDocumentDto } from '../../../core/services/practice-document.service';

/**
 * WHERE the picker is being shown, which is the only thing it needs to know about its host.
 *
 * Named for the SEND it is part of, not for the component that embeds it — `invite` is the same
 * context whether it is reached from patient creation or from the invite wizard, and both pre-tick
 * from the same flag.
 *
 * `appointment` and `whatsapp` are §14.3's remaining two hosts. They are here in wave 1, with
 * their pre-ticking, so wave 2 wires a host onto a rule that is already proven rather than
 * inventing one at the same time as the screen.
 */
export type DocumentAttachmentContext =
  'invite' | 'consentRequest' | 'manual' | 'appointment' | 'whatsapp';

/**
 * `documentId` -> *"3 days before the appointment"*.
 *
 * Only the `appointment` context fills this. It is the half of a trigger-linked chip a
 * receptionist actually needs before overriding it: WHICH document is going is on the chip
 * already, WHEN it would go is not deducible from anything else on the screen.
 */
export interface DocumentAttachmentTriggerMap { [documentId: string]: string; }

/** Everything one load produced. `documents` is already filtered by `isOfferable`. */
export interface DocumentAttachmentSource {
  documents: PracticeDocumentDto[];
  triggers: DocumentAttachmentTriggerMap;

  /**
   * The documents could not be read. NOT the same as a practice with none — see §14 of
   * `tasks/lessons.md`, and the §17 correction on the settings tab that this repeats.
   */
  loadError: string | null;

  /**
   * The documents were read but the appointment type's TRIGGERS were not, so nothing is
   * pre-ticked that otherwise would have been. A separate field because it is a separate fact:
   * the user can still attach by hand, and telling them the whole thing failed would be false.
   */
  triggerError: string | null;
}

export interface DocumentAttachmentLoadOptions {
  context: DocumentAttachmentContext;

  /**
   * The PATIENT'S practice. Omit it and the service falls back to the practice the user has
   * selected — right for a settings-side host, wrong for a patient in another practice.
   */
  groupId?: string | null;

  /** `appointment` only. Absent means no triggers and nothing pre-ticked, not a failure. */
  appointmentTypeId?: string | null;
}

/**
 * WHAT THE ATTACH DIALOG IS HANDED.
 *
 * It is given its rows rather than loading them, so the chip row and the dialog can never
 * disagree about what the practice has. `DocumentAttachmentService.choose` is the entry point for
 * a host that has no chip row and therefore no rows to hand over — it loads, then opens this.
 */
export interface DocumentAttachmentDialogData {
  context: DocumentAttachmentContext;
  documents: PracticeDocumentDto[];
  /** What is ticked when it opens. The dialog works on a COPY; Cancel changes nothing. */
  selection: string[];
  triggers?: DocumentAttachmentTriggerMap;
}

/**
 * WHAT IT CLOSES WITH.
 *
 * `undefined` — the `mat-dialog` default — means cancelled, and a host must treat it as "leave the
 * selection alone" rather than as an empty selection. The two are different answers and only one
 * of them unticks everything.
 */
export interface DocumentAttachmentDialogResult {
  selection: string[];
  /** The rows behind those ids, so a host never has to turn an id back into a document. */
  documents: PracticeDocumentDto[];
}

/** One chip in the row. Everything the chip renders, computed once. */
export interface DocumentAttachmentChip {
  id: string;
  name: string;
  /** `null` unless a trigger put this document here — see `DocumentAttachmentTriggerMap`. */
  trigger: string | null;
  requiresResponse: boolean;
  responseSentence: string;
  responseIcon: string;
}

/** The failure sentence. A practice with no documents is not a failure — see `emptyMessage`. */
export const LOAD_FAILED =
  'The practice\'s documents could not be loaded, so none can be attached. Try again in a moment.';

/** The other failure, which leaves the control usable. */
export const TRIGGERS_FAILED =
  'This appointment type\'s document triggers could not be read, so nothing has been attached '
  + 'automatically. You can still attach documents yourself.';

/**
 * `manual` and `whatsapp` offer only what the practice has allowed to be sent BY HAND.
 *
 * That is a GATE, not a pre-tick: `allowManualSend: false` means the server answers 422
 * `MANUAL_SEND_DISABLED` for that document, so offering it would be offering a send that cannot
 * happen. `whatsapp` is gated with it because a WhatsApp attachment IS a send by hand, started by
 * a user in a chat, and the failure modes are not symmetrical — a document wrongly withheld is
 * visible and annoying, a send that 422s after the user has committed to it is neither.
 *
 * Every other context is a route the practice configured elsewhere and this flag says nothing
 * about them.
 */
export function isOfferable(document: PracticeDocumentDto,
  context: DocumentAttachmentContext): boolean {
  // `listAttachableDocuments` asks for `active=true`, so this is belt and braces — but an inactive
  // document is one the practice has switched OFF (§3.1), and offering one because a filter was
  // dropped somewhere between here and the store would send a patient a blank the practice
  // believes is retired. It costs one comparison to make that unreachable.
  if (document.active === false) return false;
  if (context !== 'manual' && context !== 'whatsapp') return true;
  return document.allowManualSend !== false;
}

/**
 * CONFIGURATION PRE-TICKS; IT NEVER FORCES (§14.3).
 *
 * `invite` from `sendWithInvite`, `consentRequest` from `sendWithConsentRequest`, `appointment`
 * from the appointment type's ACTIVE links, `manual` and `whatsapp` from nothing at all.
 *
 * A `requiredInInvite` document is pre-ticked by `sendWithInvite` like any other and is untickable
 * like any other: this control is about what the PRACTICE sends, not about what the patient must
 * return, and those are two different decisions that happen to touch the same row.
 */
export function preTickedIds(source: DocumentAttachmentSource,
  context: DocumentAttachmentContext): string[] {
  return source.documents
    .filter((document) => {
      if (context === 'invite') return !!document.sendWithInvite;
      if (context === 'consentRequest') return !!document.sendWithConsentRequest;
      // A trigger is the appointment's configuration, in exactly the sense the two flags above are
      // the invite's and the consent request's.
      if (context === 'appointment') return !!source.triggers[document.id];
      return false;
    })
    .map((document) => document.id);
}

/** The empty-state sentence, which depends on the context that produced it. */
export function emptyMessage(context: DocumentAttachmentContext): string {
  return (context === 'manual' || context === 'whatsapp')
    ? 'This practice has no documents that can be sent by hand. Upload one under '
      + 'Settings > Documents, and switch on manual sending.'
    : 'This practice has no documents to attach. Upload one under Settings > Documents.';
}
